import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { classQueryKeys, normalizeSchoolClass } from '../constants/classes';

const REGISTRY_URL = `${process.env.PUBLIC_URL || ''}/data/students-registry.json`;

let registryCache = null;
let registryPromise = null;

function mapStudentRow(s) {
  return {
    studentId: s.studentId || s.admno,
    studentName: s.studentName || s.name || s.studentId,
    admno: s.admno || s.studentId,
    studentCsvId: s.studentCsvId || null,
    class: s.class || '',
  };
}

function sortStudents(list) {
  return [...list].sort((a, b) =>
    String(a.studentName || '').localeCompare(String(b.studentName || ''), undefined, { sensitivity: 'base' })
  );
}

async function loadStaticRegistry() {
  if (registryCache) return registryCache;
  if (!registryPromise) {
    registryPromise = fetch(REGISTRY_URL, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : { students: [] }))
      .then((data) => {
        registryCache = (data.students || []).map(mapStudentRow);
        return registryCache;
      })
      .catch(() => {
        registryCache = [];
        return registryCache;
      });
  }
  return registryPromise;
}

async function listStudentsFromFirestore(className) {
  if (!db) return [];
  const keys = classQueryKeys(className);
  if (!keys.length) return [];
  const byId = new Map();
  for (const classKey of keys) {
    const q = query(collection(db, 'students'), where('class', '==', classKey));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      byId.set(d.id, {
        studentId: d.id,
        studentName: d.data().name || d.id,
        admno: d.data().admno || d.id,
        studentCsvId: d.data().studentCsvId || null,
        class: normalizeSchoolClass(d.data().class) || d.data().class || classKey,
      });
    });
  }
  return sortStudents([...byId.values()]);
}

/** Students in a class — Firestore first, then static school registry JSON. */
export async function listStudentsByClass(className) {
  const classKey = String(className || '').trim();
  if (!classKey) return [];

  const fromDb = await listStudentsFromFirestore(classKey);
  if (fromDb.length > 0) return fromDb;

  const all = await loadStaticRegistry();
  const keys = new Set(classQueryKeys(classKey));
  return sortStudents(
    all.filter((s) => keys.has(normalizeSchoolClass(s.class) || s.class) || keys.has(s.class))
  );
}

function classFromRegistry(profile, all) {
  const ids = [...new Set([profile.admno, profile.studentCsvId, profile.id].filter(Boolean).map(String))];
  const hit = all.find((s) =>
    ids.some(
      (id) =>
        String(s.studentId) === id ||
        String(s.admno) === id ||
        (s.studentCsvId && String(s.studentCsvId) === id)
    )
  );
  return hit ? normalizeSchoolClass(hit.class) || hit.class : '';
}

/** Resolve assigned class for a student login profile. */
export async function getStudentClassForProfile(profile) {
  if (!profile) return '';
  if (profile.class) return normalizeSchoolClass(profile.class) || profile.class;
  if (profile.role === 'STUDENT') {
    return classFromRegistry(profile, await loadStaticRegistry());
  }
  const ids = [...new Set([profile.admno, profile.studentCsvId, profile.id].filter(Boolean).map(String))];
  if (db) {
    for (const id of ids) {
      const snap = await getDoc(doc(db, 'students', id));
      if (snap.exists()) {
        const c = snap.data().class;
        return normalizeSchoolClass(c) || c || '';
      }
    }
    for (const id of ids) {
      const q = query(collection(db, 'students'), where('admno', '==', id));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const c = snap.docs[0].data().class;
        return normalizeSchoolClass(c) || c || '';
      }
    }
  }
  return classFromRegistry(profile, await loadStaticRegistry());
}

/** All students — Firestore first, then static registry. */
export async function listAllStudents() {
  if (db) {
    const snap = await getDocs(collection(db, 'students'));
    if (!snap.empty) {
      return sortStudents(
        snap.docs.map((d) => ({
          studentId: d.id,
          studentName: d.data().name || d.id,
          admno: d.data().admno || d.id,
          class: d.data().class || '',
          studentCsvId: d.data().studentCsvId || null,
        }))
      );
    }
  }
  return sortStudents(await loadStaticRegistry());
}

/** Clear cached static registry (after admin re-import). */
export function clearStudentRegistryCache() {
  registryCache = null;
  registryPromise = null;
}
